import React, { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ChevronLeft, Search } from "lucide-react";
import toast from "react-hot-toast";
import { useRecommendationsInbox } from "../../hooks/scout-request/useRecommendationsInbox";
import RecommendationCard from "../../components/scout-request/RecommendationCard";
import ScoutShareModal from "../../components/scout-request/ScoutShareModal";
import { StatusPill } from "../../components/scout-request/ScoutPills";
import { formatPositionAge, formatScoutHorizon } from "../../utils/scoutFormat";
import ScoutInfoDot from "../../components/scout-request/ScoutInfoDot";
import { scoutRequestService } from "../../services/scoutRequestService";

const RecommendationsInbox = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const {
    request,
    recommendations,
    loading,
    error,
    fetchInbox,
    markViewed,
    addToRadar,
    undoAddToRadar,
    dismiss,
    undoDismiss,
  } = useRecommendationsInbox(id);

  // Snapshot which recs were new on first load — the highlight + "New" pill stay
  // for this viewing session even as mark_viewed persists server-side, so they
  // don't flicker away under the reader. Cleared next visit.
  const [newIds, setNewIds] = useState(null);
  const snappedRef = useRef(false);
  useEffect(() => {
    if (!snappedRef.current && !loading && recommendations.length) {
      snappedRef.current = true;
      const ids = new Set(recommendations.filter((r) => r.is_new).map((r) => r.id));
      setNewIds(ids);
      recommendations.filter((r) => r.is_new).forEach((r) => markViewed(r));
    }
  }, [loading, recommendations, markViewed]);

  const act = async (fn, rec, errMsg) => {
    const res = await fn(rec);
    if (!res.success) toast.error(res.error || errMsg);
  };

  const [forwarding, setForwarding] = useState(null);

  return (
    <div className="min-h-screen bg-[#EDF0F4]">
      {/* Platform-standard container (W1-04): no fixed max-width, page
          scales to 2560+ via the progressive padding ladder. */}
      <div className="mx-auto px-4 md:px-6 lg:px-7 xl:px-8 min-[1920px]:px-10 min-[2560px]:px-12 py-6">
        <button
          onClick={() => navigate("/scouting/requests")}
          className="inline-flex items-center gap-1 text-[13px] font-semibold text-[#6B7280] hover:text-[#111827] mb-4"
        >
          <ChevronLeft className="w-4 h-4" />
          Scout requests
        </button>

        {/* Request header card */}
        {request && (
          <div className="bg-white rounded-xl p-5 border border-[#E5E7EB] mb-5">
            <div className="flex items-center gap-2 flex-wrap mb-1">
              <p className="text-[16px] font-bold text-[#111827]">{formatPositionAge(request)}</p>
              <StatusPill status={request.status} />
              <ScoutInfoDot
                id="club-inbox"
                title="Your recommendations inbox"
                body="Recommendations from agents land here. Add to radar moves a player to the radar."
              />
            </div>
            <p className="text-[12.5px] text-[#6B7280]">
              Horizon: {formatScoutHorizon(request.horizon)}
              {request.level_expectation ? ` · ${request.level_expectation}` : ""}
            </p>
            {request.counts && (
              <p className="text-[12.5px] font-semibold text-[#111827] mt-2">
                {request.counts.total} recommendation{request.counts.total === 1 ? "" : "s"}
                {request.counts.new > 0 && <span className="text-[#1B6FE0]"> · {request.counts.new} new</span>}
              </p>
            )}
          </div>
        )}

        {/* Content */}
        {loading ? (
          <div className="flex flex-col gap-4">
            {Array.from({ length: 2 }).map((_, i) => (
              <div key={i} className="bg-white rounded-xl p-5 border border-[#E5E7EB] animate-pulse h-32" />
            ))}
          </div>
        ) : error ? (
          <div className="flex flex-col items-center py-16 text-center">
            <p className="text-red-500 mb-3">{error}</p>
            <button onClick={() => fetchInbox(1)} className="px-4 py-2 border rounded-lg hover:bg-gray-50">
              Try again
            </button>
          </div>
        ) : recommendations.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-20 px-4 text-center">
            <div className="bg-white border border-[#E5E7EB] rounded-full p-4 mb-4">
              <Search className="w-7 h-7 text-[#9CA3AF]" />
            </div>
            <h3 className="text-[17px] font-bold text-[#111827] mb-1.5">No recommendations yet</h3>
            <p className="text-[13px] text-[#6B7280] max-w-sm">
              Agents are looking. Their recommendations will land here, visible only to you.
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-4">
            {recommendations.map((rec) => (
              <RecommendationCard
                key={rec.id}
                rec={rec}
                isNew={newIds ? newIds.has(rec.id) : rec.is_new}
                onAdd={(r) => act(addToRadar, r, "Could not add to the radar.")}
                onUndoAdd={(r) => act(undoAddToRadar, r, "Could not undo.")}
                onDismiss={(r) => act(dismiss, r, "Could not dismiss.")}
                onUndoDismiss={(r) => act(undoDismiss, r, "Could not undo.")}
                onShare={setForwarding}
              />
            ))}
          </div>
        )}
      </div>

      <ScoutShareModal
        open={!!forwarding}
        title="Forward to chat"
        onClose={() => setForwarding(null)}
        onSend={(channelId, text) =>
          scoutRequestService.forwardRecommendation(forwarding.id, { channelId, text })
        }
      />
    </div>
  );
};

export default RecommendationsInbox;
